import { OutboxIdempotencyConflictError } from "@econ-v1/ports";
import { canonicalJson, repositoryMutation, requiredString, } from "./transaction.js";
export class OutboxLeaseConflictError extends Error {
    id;
    constructor(id) {
        super("Outbox lease is no longer owned by this replay worker");
        this.name = "OutboxLeaseConflictError";
        this.id = id;
    }
}
export class SqliteOutboxRepository {
    #database;
    #now;
    constructor(database, options = {}) {
        this.#database = database;
        this.#now = options.now ?? (() => new Date().toISOString());
    }
    create(input) {
        return repositoryMutation(this.#database, (transaction) => {
            const duplicate = transaction.selectObject("SELECT domain, payload_json FROM outbox WHERE idempotency_key = ?", [input.idempotencyKey]);
            if (duplicate !== undefined) {
                const sameDomain = requiredString(duplicate.domain, "outbox domain") === input.domain;
                const storedPayload = parseJson(requiredString(duplicate.payload_json, "outbox payload_json"));
                if (sameDomain && canonicalJson(storedPayload) === canonicalJson(input.payload))
                    return undefined;
                throw new OutboxIdempotencyConflictError(input.idempotencyKey);
            }
            const replayOrder = Number(transaction.selectValue("SELECT COALESCE(MAX(replay_order), 0) + 1 FROM outbox"));
            transaction.exec(`INSERT INTO outbox(
          id, domain, payload_json, idempotency_key, state, replay_order,
          lease_expires_at, attempt_count, created_at, updated_at
        ) VALUES (?, ?, ?, ?, ?, ?, NULL, 0, ?, ?)`, { bind: [
                    input.id,
                    input.domain,
                    JSON.stringify(input.payload),
                    input.idempotencyKey,
                    databaseState(input.state),
                    replayOrder,
                    input.createdAt,
                    input.updatedAt,
                ] });
            return {
                entityId: input.id,
                event: outboxEvent(input, this.#now()),
            };
        });
    }
    delete(id) {
        return repositoryMutation(this.#database, (transaction) => {
            const current = readOutbox(transaction, id);
            if (current === undefined)
                return undefined;
            transaction.exec("DELETE FROM outbox WHERE id = ?", { bind: [id] });
            return {
                entityId: id,
                event: {
                    occurredAt: this.#now(),
                    payload: { action: "deleted", entityId: id, entityType: "outbox-intent" },
                    type: "domain.entity-changed",
                },
            };
        });
    }
    async list(filter = {}) {
        const clauses = [];
        const bind = [];
        if (filter.domain !== undefined) {
            clauses.push("domain = ?");
            bind.push(filter.domain);
        }
        if (filter.state !== undefined) {
            clauses.push("state = ?");
            bind.push(databaseState(filter.state));
        }
        const where = clauses.length === 0 ? "" : ` WHERE ${clauses.join(" AND ")}`;
        return this.#database.selectObjects(`SELECT id, domain, payload_json, idempotency_key, state, created_at, updated_at
        FROM outbox${where} ORDER BY replay_order`, bind).map(outboxRecord);
    }
    async read(id) {
        return readOutbox(this.#database, id);
    }
    async claimHead(input) {
        const now = this.#now();
        return this.#database.transaction("IMMEDIATE", (transaction) => {
            const head = transaction.selectObject(`SELECT id FROM outbox
          WHERE domain = ? AND state NOT IN ('acked', 'failed', 'conflict')
          ORDER BY replay_order LIMIT 1`, [input.domain]);
            if (head === undefined)
                return undefined;
            const id = requiredString(head.id, "outbox id");
            const eligible = transaction.selectObject(`SELECT id FROM outbox
          WHERE id = ? AND domain = ? AND (
            (state = 'pending' AND (next_attempt_at IS NULL OR next_attempt_at <= ?)) OR
            (state = 'inflight' AND (lease_expires_at IS NULL OR lease_expires_at <= ?))
          )`, [id, input.domain, now, now]);
            if (eligible === undefined)
                return undefined;
            transaction.exec(`UPDATE outbox SET
          state = 'inflight', lease_token = ?, lease_expires_at = ?,
          attempt_count = attempt_count + 1, updated_at = ?, settled_at = NULL
          WHERE id = ? AND domain = ?`, { bind: [input.leaseToken, input.leaseExpiresAt, now, id, input.domain] });
            const claim = readReplayClaim(transaction, id);
            if (claim === undefined)
                throw new Error("Claimed outbox intent was not found");
            return claim;
        });
    }
    async listStatus(filter = {}) {
        const bind = [];
        const where = filter.domain === undefined ? "" : " WHERE domain = ?";
        if (filter.domain !== undefined)
            bind.push(filter.domain);
        return this.#database.selectObjects(`SELECT id, domain, state, attempt_count, next_attempt_at,
          last_error_code, last_error_message, terminal_error_code,
          terminal_error_message, settled_at, created_at, updated_at
        FROM outbox${where} ORDER BY replay_order`, bind).map(replayStatus);
    }
    async settle(input) {
        const now = this.#now();
        return this.#database.transaction("IMMEDIATE", (transaction) => {
            const current = transaction.selectObject("SELECT state, lease_token FROM outbox WHERE id = ?", [input.id]);
            if (current === undefined)
                throw new Error("Outbox intent was not found");
            if (requiredString(current.state, "outbox state") !== "inflight" ||
                requiredString(current.lease_token, "outbox lease_token") !== input.leaseToken)
                throw new OutboxLeaseConflictError(input.id);
            const error = "error" in input ? {
                code: sanitizeErrorPart(input.error.code),
                message: sanitizeErrorPart(input.error.message),
            } : undefined;
            switch (input.outcome) {
                case "acknowledged":
                    transaction.exec(`UPDATE outbox SET state = 'acked', lease_token = NULL,
              lease_expires_at = NULL, next_attempt_at = NULL,
              last_error_code = NULL, last_error_message = NULL,
              terminal_error_code = NULL, terminal_error_message = NULL,
              settled_at = ?, updated_at = ? WHERE id = ?`, { bind: [now, now, input.id] });
                    break;
                case "retry":
                    transaction.exec(`UPDATE outbox SET state = 'pending', lease_token = NULL,
              lease_expires_at = NULL, next_attempt_at = ?,
              last_error_code = ?, last_error_message = ?,
              terminal_error_code = NULL, terminal_error_message = NULL,
              settled_at = NULL, updated_at = ? WHERE id = ?`, { bind: [input.retryAt, error.code, error.message, now, input.id] });
                    break;
                case "failed":
                case "conflict":
                    transaction.exec(`UPDATE outbox SET state = ?, lease_token = NULL,
              lease_expires_at = NULL, next_attempt_at = NULL,
              last_error_code = NULL, last_error_message = NULL,
              terminal_error_code = ?, terminal_error_message = ?,
              settled_at = ?, updated_at = ? WHERE id = ?`, { bind: [databaseReplayState(input.outcome), error.code, error.message, now, now, input.id] });
                    break;
            }
            const updated = readOutbox(transaction, input.id);
            if (updated === undefined)
                throw new Error("Settled outbox intent was not found");
            const event = outboxEvent(updated, this.#now());
            insertRepositoryEvent(transaction, input.id, event);
            const sequence = Number(transaction.selectValue("SELECT last_insert_rowid()"));
            return { eventSequences: [sequence], events: [event] };
        });
    }
    updateState(id, state) {
        return repositoryMutation(this.#database, (transaction) => {
            const current = readOutbox(transaction, id);
            if (current === undefined)
                throw new Error("Outbox intent was not found");
            const updated = { ...current, state, updatedAt: this.#now() };
            transaction.exec("UPDATE outbox SET state = ?, updated_at = ? WHERE id = ?", { bind: [databaseState(state), updated.updatedAt, id] });
            return {
                entityId: id,
                event: outboxEvent(updated, this.#now()),
            };
        });
    }
}
function databaseState(state) {
    switch (state) {
        case "acknowledged":
            return "acked";
        case "replaying":
            return "inflight";
        case "failed":
        case "conflict":
        case "pending":
            return state;
    }
}
function databaseReplayState(state) {
    return state;
}
function outboxEvent(intent, occurredAt) {
    return { occurredAt, payload: { intent }, type: "outbox.state-changed" };
}
function insertRepositoryEvent(transaction, entityId, event) {
    transaction.exec(`INSERT INTO repository_events(event_type, entity_id, occurred_at, payload_json)
      VALUES (?, ?, ?, ?)`, { bind: [event.type, entityId, event.occurredAt, JSON.stringify(event.payload)] });
}
function outboxRecord(row) {
    const databaseValue = requiredString(row.state, "outbox state");
    return {
        createdAt: requiredString(row.created_at, "outbox created_at"),
        domain: requiredString(row.domain, "outbox domain"),
        id: requiredString(row.id, "outbox id"),
        idempotencyKey: requiredString(row.idempotency_key, "outbox idempotency_key"),
        payload: parseJson(requiredString(row.payload_json, "outbox payload_json")),
        state: recordState(databaseValue),
        updatedAt: requiredString(row.updated_at, "outbox updated_at"),
    };
}
function readReplayClaim(database, id) {
    const row = database.selectObject(`SELECT id, domain, payload_json, idempotency_key, state, created_at,
        updated_at, attempt_count, lease_token, lease_expires_at
      FROM outbox WHERE id = ?`, [id]);
    if (row === undefined)
        return undefined;
    const record = outboxRecord(row);
    return {
        ...record,
        attemptCount: requiredInteger(row.attempt_count, "outbox attempt_count"),
        leaseExpiresAt: requiredString(row.lease_expires_at, "outbox lease_expires_at"),
        leaseToken: requiredString(row.lease_token, "outbox lease_token"),
        state: "replaying",
    };
}
function replayStatus(row) {
    return {
        attemptCount: requiredInteger(row.attempt_count, "outbox attempt_count"),
        createdAt: requiredString(row.created_at, "outbox created_at"),
        domain: requiredString(row.domain, "outbox domain"),
        id: requiredString(row.id, "outbox id"),
        ...(optionalString(row.last_error_code, "lastErrorCode", "outbox last_error_code")),
        ...(optionalString(row.last_error_message, "lastErrorMessage", "outbox last_error_message")),
        ...(optionalString(row.next_attempt_at, "nextAttemptAt", "outbox next_attempt_at")),
        ...(optionalString(row.settled_at, "settledAt", "outbox settled_at")),
        state: replayState(requiredString(row.state, "outbox state")),
        ...(optionalString(row.terminal_error_code, "terminalErrorCode", "outbox terminal_error_code")),
        ...(optionalString(row.terminal_error_message, "terminalErrorMessage", "outbox terminal_error_message")),
        updatedAt: requiredString(row.updated_at, "outbox updated_at"),
    };
}
function parseJson(json) {
    const value = JSON.parse(json);
    if (!isRepositoryJsonValue(value))
        throw new TypeError("Stored outbox payload is not JSON");
    return value;
}
function optionalString(value, key, field) {
    return value === null || value === undefined ? {} : { [key]: requiredString(value, field) };
}
function requiredInteger(value, field) {
    if (typeof value !== "number" || !Number.isSafeInteger(value) || value < 0) {
        throw new TypeError(`Expected ${field} to be a non-negative integer`);
    }
    return value;
}
function sanitizeErrorPart(value) {
    const sanitized = value.trim().slice(0, 256);
    if (sanitized === "")
        throw new TypeError("Outbox error details must not be empty");
    return sanitized;
}
function isRepositoryJsonValue(value) {
    if (value === null || typeof value === "boolean" || typeof value === "string")
        return true;
    if (typeof value === "number")
        return Number.isFinite(value);
    if (Array.isArray(value))
        return value.every(isRepositoryJsonValue);
    if (typeof value !== "object")
        return false;
    return Object.values(value).every(isRepositoryJsonValue);
}
function readOutbox(database, id) {
    const row = database.selectObject(`SELECT id, domain, payload_json, idempotency_key, state, created_at, updated_at
      FROM outbox WHERE id = ?`, [id]);
    return row === undefined ? undefined : outboxRecord(row);
}
function recordState(state) {
    switch (state) {
        case "acked":
            return "acknowledged";
        case "inflight":
            return "replaying";
        case "failed":
        case "conflict":
        case "pending":
            return state;
        default:
            throw new TypeError("Stored outbox state is invalid");
    }
}
function replayState(state) {
    switch (state) {
        case "acked": return "acknowledged";
        case "inflight": return "replaying";
        case "pending":
        case "failed":
        case "conflict":
            return state;
        default:
            throw new TypeError("Stored outbox replay state is invalid");
    }
}
//# sourceMappingURL=outbox.js.map